/* eslint-disable camelcase */
import pick from 'lodash-es/pick';
import pickBy from 'lodash-es/pickBy';
import merge from 'lodash-es/merge';

import {
  ListingCostsCosts,
  ListingCosts,
  ListingCost,
} from '@ha/api/v2/types/Listing';
import {
  CostsDepositKeys,
  CostsUtilityKeys,
  CostsAdditionalKeys,
  CostsOptionalKeys,
  CostsUtilityCompatibilityKey,
} from 'ha/models/ListingCosts/types';

/**
 * The sum of all required deposits
 */
export const getRequiredDepositsTotal = (costs: ListingCosts): number => {
  let total = 0;

  Object.values(CostsDepositKeys).forEach(deposit => {
    if (costs.costs[deposit]) {
      total += costs.costs[deposit]!.value;
    }
  });

  return total;
};

/**
 * Returns a collection of costs excluding deposits
 */
export const getDuringYourStayCosts = (
  costs: ListingCosts,
): ListingCostsCosts => {
  const utilityCosts: ListingCostsCosts = pick(
    costs.costs,
    Object.values(CostsUtilityKeys),
  );

  const allUtilityCosts: ListingCostsCosts = {};

  // Iterate all values of the utility costs and make sure they are all present, even if some are missing.
  // except for estimated and broadcasting fees
  Object.values(CostsUtilityKeys).forEach(costKey => {
    if (utilityCosts[costKey]) {
      allUtilityCosts[costKey] = utilityCosts[costKey];
    } else if (
      !costs.costs[CostsUtilityCompatibilityKey.UTILITY_BILLS] &&
      // Skip broadcasting fee if not specified
      costKey !== CostsUtilityKeys.BROADCASTING
    ) {
      // If it's not present, create a mock cost to show the "unknown" string in LDP
      allUtilityCosts[costKey] = {
        value: 0,
        payableAt: 'monthly',
        payableBy: 'unknown',
        required: false,
        refundable: false,
        isEstimated: false,
      } as ListingCost;
    }
  });

  if (costs.costs[CostsUtilityCompatibilityKey.UTILITY_BILLS]) {
    allUtilityCosts[CostsUtilityCompatibilityKey.UTILITY_BILLS] =
      costs.costs[CostsUtilityCompatibilityKey.UTILITY_BILLS];
  }

  return merge(
    allUtilityCosts,
    pick(costs.costs, Object.values(CostsAdditionalKeys)),
  );
};

/**
 * Returns a collection of all optional costs and the deposits marked as not required
 */
export const getOptionalCostsEnumerated = (
  costs: ListingCosts,
): ListingCostsCosts => {
  const optionalCosts = pick(costs.costs, [
    ...Object.values(CostsOptionalKeys),
  ]);

  const optionalDepositCosts = pickBy(
    costs.costs,
    (cost, key) =>
      cost?.required === false && key !== CostsUtilityKeys.BROADCASTING,
  );

  return merge(optionalCosts, optionalDepositCosts);
};
